import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Location } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { EvaService } from "src/app/core/services/eva.service";
import { AiChatService } from "src/app/features/ai-chat/services/ai-chat.service";
import { AiDrawerSession } from "src/app/features/ai-chat/services/ai-drawer-session.service";
import { EvaFeatures } from "src/app/features/eva-company/domains/eva-company";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { Note, NoteContext } from "src/app/domains/note";
import { NoteContextService } from "src/app/services/note-context.service";
import { NoteService } from "src/app/services/note.service";
import { TaskService } from "src/app/features/task/services/task.service";
import { TaskStatus } from "src/app/features/task/domains/task";
import { ToggleOption } from "src/app/shared/ds/eva-toggle-switch/eva-toggle-switch.component";
import { Subscription } from "rxjs";
import { filter, startWith } from "rxjs/operators";
import { DrawerConversationsComponent } from "./drawer-conversations/drawer-conversations.component";
import { NotesListComponent } from "./notes-list/notes-list.component";
import { hasOpenEvaModal } from "../../shared/ds/eva-dialog/eva-modal-stack";

type DrawerView = "tasks" | "notes" | "conversations" | "assistant";

/**
 * EVA Notes & Todos - Composant flottant
 *
 * Orb flottant en bas a droite ouvrant un drawer lateral
 * avec deux vues : Todos et Notes (via toggle switch).
 * Gere son propre etat open/closed en interne.
 *
 * @example
 * <app-eva-notes-todos></app-eva-notes-todos>
 */
@Component({
  selector: "app-eva-notes-todos",
  templateUrl: "./eva-notes-todos.component.html",
  styleUrls: ["./eva-notes-todos.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaNotesTodosComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private evaCompanyService = inject(EvaCompanyService);
  private aiChatService = inject(AiChatService);
  /** Onglet Assistant : réflexion en cours et réponse non lue, galet fermé compris (signaux, lus en OnPush). */
  readonly aiSession = inject(AiDrawerSession);

  /** Onglet Assistant : pour qui a accès à l'assistant (remplace l'entrée du menu de gauche). */
  get hasAssistant(): boolean {
    return this.aiChatService.available;
  }

  /** Page /ai : l'assistant y est déjà en pleine page, l'onglet du galet disparaît. */
  onAiPage = false;

  /** Onglet Assistant affiché : accès à l'assistant, hors de la page /ai. */
  get showAssistantTab(): boolean {
    return this.hasAssistant && !this.onAiPage;
  }

  /** Onglet mis en évidence dans le galet : seulement quand la fenêtre est ouverte. */
  get selectedView(): DrawerView | null {
    return this.isOpen ? this.activeView : null;
  }

  /** Conversations sorties de V3_BETA : onglet visible seulement avec la feature CONVERSATIONS */
  get hasConversations(): boolean {
    return this.evaCompanyService.currentEvaCompany?.hasFeature(EvaFeatures.CONVERSATIONS) ?? false;
  }

  // ==========================================================================
  // State
  // ==========================================================================

  /** Drawer ouvert ou ferme */
  isOpen: boolean = false;

  /** Barre repliée ou déployée */
  isCollapsed: boolean = localStorage.getItem("eva-notes-todos-collapsed") === "true";

  /** Vue active (persistée en localStorage) */
  activeView: DrawerView | null = localStorage.getItem("eva-notes-todos-view") as DrawerView | null;

  /** Badges de notification par onglet */
  tasksCount: number = 0;
  /** Tâches en retard : le badge les affiche en rouge, sinon le total en gris (même règle que la topbar) */
  tasksOverdue = 0;
  private tasksCountSub?: Subscription;

  get tasksBadge(): string | number | null {
    const value = this.tasksOverdue || this.tasksCount;
    return value > 0 ? (value > 99 ? "99+" : value) : null;
  }
  conversationsCount: number = 0;

  /** Total des notifications (tous onglets) */
  get totalNotifications(): number {
    return this.tasksCount + (this.hasConversations ? this.conversationsCount : 0) + (this.aiSession.unread() ? 1 : 0);
  }

  /** Mode de la vue Notes : liste ou detail */
  noteViewMode: "list" | "detail" = "list";

  /** ID de la note en cours de consultation/edition */
  currentNoteId: string | null = null;

  /** Contexte détecté depuis l'URL courante */
  routeContext: NoteContext | null = null;

  /** Contexte objet courant passé au drawer-tasks (type + id + label) */
  currentObjectContext: { object_id: string; object_type: string; object_name?: string } | null = null;

  /** Contexte de rattachement actif */
  @Input() context: NoteContext | null = null;

  @ViewChild("notesList") notesList?: NotesListComponent;
  @ViewChild("conversationsRef") conversationsRef?: DrawerConversationsComponent;

  /**
   * Largeur de la barre flottante publiée dans `--eva-floating-bar-width` : les barres d'actions
   * collées en bas à droite (chiffrage de l'offre) la réservent pour ne pas passer dessous
   */
  @ViewChild("floatingBar") set floatingBar(ref: ElementRef<HTMLElement> | undefined) {
    this.floatingBarObserver?.disconnect();
    if (!ref) return;
    const root = document.documentElement.style;
    this.floatingBarObserver = new ResizeObserver(([entry]) =>
      root.setProperty("--eva-floating-bar-width", `${entry.target.getBoundingClientRect().width}px`),
    );
    this.floatingBarObserver.observe(ref.nativeElement);
  }
  private floatingBarObserver?: ResizeObserver;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private location: Location,
    private noteService: NoteService,
    private noteContextService: NoteContextService,
    private taskService: TaskService,
    public evaService: EvaService,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  private lastSyncedPath: string | null = null;

  /** Appelé par NavigationEnd et Location.onUrlChange : une seule fois par chemin (hors query params) */
  private syncRouteContext(url: string): void {
    const path = "/" + (url || "").replace(/^#?\/?/, "").split(/[?#]/)[0];
    if (path === this.lastSyncedPath) return;
    this.lastSyncedPath = path;
    this.onAiPage = path === "/ai";
    if (this.onAiPage && this.isOpen && this.activeView === "assistant") this.close();
    this.routeContext = this.noteContextService.resolveContextFromUrl(url);
    if (this.routeContext) {
      this.noteContextService.setContextObject(this.routeContext);
    }
    this.currentObjectContext = this.routeContext
      ? {
          object_id: this.routeContext.objectId,
          object_type: this.routeContext.objectType,
          object_name: this.routeContext.objectLabel,
        }
      : null;
    this.refreshTasksCount();
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    // Garder un contexte route-based prêt à l'ouverture de l'orb
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncRouteContext(this.router.url));

    // Retour à une liste par location.go (flèche retour de l'offre) : pas de NavigationEnd,
    // sans quoi le galet garderait le contexte de l'objet quitté
    const stopUrlListener = this.location.onUrlChange((url) => this.syncRouteContext(url));
    this.destroyRef.onDestroy(stopUrlListener);
    this.destroyRef.onDestroy(() => {
      this.floatingBarObserver?.disconnect();
      document.documentElement.style.removeProperty("--eva-floating-bar-width");
    });

    // Mettre à jour object_name dès qu'un module enrichit le contexte (ex: customer-details-form)
    this.noteContextService.context$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ctx) => {
      if (
        ctx &&
        this.currentObjectContext &&
        ctx.objectId === this.currentObjectContext.object_id &&
        ctx.objectLabel &&
        ctx.objectLabel !== this.currentObjectContext.object_name
      ) {
        this.currentObjectContext = { ...this.currentObjectContext, object_name: ctx.objectLabel };
        this.cdr.markForCheck();
      }
    });

    // Ecouter les demandes d'ouverture du drawer avec un contexte
    this.noteContextService.drawerOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ctx) => {
      this.context = ctx;
      if (ctx) {
        this.noteContextService.setContextObject(ctx);
      }
      this.activeView = "notes";
      this.noteViewMode = "list";
      this.currentNoteId = null;
      this.open();
    });

    // Ecouter les demandes d'ouverture du drawer Tâches avec un contexte
    this.noteContextService.drawerTasksOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ctx) => {
      if (ctx) {
        this.currentObjectContext = ctx;
      }
      this.activeView = "tasks";
      this.open();
      this.refreshTasksCount();
    });

    // Vue mémorisée d'une session où la feature Conversations (ou l'accès à l'assistant) était active
    if (
      (this.activeView === "conversations" && !this.hasConversations) ||
      (this.activeView === "assistant" && !this.hasAssistant)
    ) {
      this.activeView = null;
      localStorage.removeItem("eva-notes-todos-view");
    }

    this.noteContextService.drawerConversationsOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.hasConversations) return;
      this.activeView = "conversations";
      this.open();
    });

    this.noteContextService.drawerAssistantOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.hasAssistant) return;
      this.activeView = "assistant";
      localStorage.setItem("eva-notes-todos-view", "assistant");
      this.open();
    });

    this.taskService.tasksChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshTasksCount());
    this.refreshConversationsCount();
  }

  /**
   * Compteur de la barre : tâches actives de l'objet de la page s'il y en a un,
   * sinon les miennes (EP-1120). Même chiffre que l'onglet du galet et le bouton de l'objet.
   */
  private refreshTasksCount(): void {
    const ctx = this.currentObjectContext;
    const tasks$ = ctx?.object_id
      ? this.taskService.getTasksByObject(ctx.object_type.toUpperCase(), ctx.object_id)
      : this.taskService.getMyTasks();
    this.tasksCountSub?.unsubscribe();
    this.tasksCountSub = tasks$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tasks) => {
        const active = tasks.filter((t) => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS);
        this.tasksCount = active.length;
        this.tasksOverdue = active.filter((t) => t.isOverdue()).length;
        this.cdr.markForCheck();
      },
      error: () => {
        this.tasksCount = 0;
        this.tasksOverdue = 0;
        this.cdr.markForCheck();
      },
    });
  }

  private refreshConversationsCount(): void {
    // TODO: récupérer le nombre de messages non lus via ConversationService
    // Pour l'instant, valeur hardcodée pour démonstration
    this.conversationsCount = 0;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Keyboard
  // ==========================================================================

  @HostListener("document:keydown.escape", ["$event"])
  onEscapeKey(event: KeyboardEvent): void {
    if (event.defaultPrevented || hasOpenEvaModal()) return;
    // Échap dans un champ du galet : on sort du champ, le galet reste ouvert (EP-1120)
    const target = event.target as HTMLElement | null;
    if (this.isOpen && target?.matches?.("input, textarea, [contenteditable='true']")) {
      target.blur();
      return;
    }
    // Saisie en cours hors du focus (brouillon de tâche, nom de lot) : Échap ne ferme pas le galet
    if (this.isOpen && this.hasDraftInProgress()) return;
    if (this.isOpen) {
      this.close();
    }
  }

  /**
   * Un titre de tâche tapé (création ou édition), un nom de lot en cours d'édition, ou un menu
   * ouvert dans le galet : Échap ferme d'abord le menu, pas le galet
   */
  private hasDraftInProgress(): boolean {
    const root = this.host.nativeElement;
    if (root.querySelector(".task-lot-row__title-input, .task-shell__context-menu, .task-row__menu-dropdown"))
      return true;
    return Array.from(
      root.querySelectorAll<HTMLInputElement>(
        ".task-inline-create__title-input input, .task-inline-edit__title-input input",
      ),
    ).some((input) => input.value.trim() !== "");
  }

  // ==========================================================================
  // Drawer control
  // ==========================================================================

  /** Ouvre le drawer avec une vue spécifique */
  openWithView(view: DrawerView): void {
    // Si déjà ouvert sur cette vue, fermer et désélectionner
    if (this.isOpen && this.activeView === view) {
      this.close();
      this.activeView = null;
      localStorage.removeItem("eva-notes-todos-view");
      this.cdr.markForCheck();
      return;
    }

    // Définir la vue active
    this.activeView = view;
    localStorage.setItem("eva-notes-todos-view", view);

    // Rafraîchir le contexte de page (section "Sur cette page"), sans filtrer la liste
    if (view === "notes") {
      this.routeContext = this.noteContextService.resolveContextFromUrl(this.router.url);
      if (this.routeContext) {
        this.noteContextService.setContextObject(this.routeContext);
      }
      this.noteViewMode = "list";
      this.currentNoteId = null;
    }

    this.open();
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
      // Désélectionner l'onglet actif
      this.activeView = null;
      localStorage.removeItem("eva-notes-todos-view");
      this.cdr.markForCheck();
      return;
    }

    // Ouverture via toggle sans onglet spécifique : ne rien faire
    // L'utilisateur doit cliquer sur un onglet pour ouvrir
  }

  open(): void {
    this.isOpen = true;
    this.cdr.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    // Reset le mode detail a la fermeture
    this.noteViewMode = "list";
    this.currentNoteId = null;
    // Ne pas réinitialiser activeView ici pour garder l'onglet actif visuellement
    this.cdr.markForCheck();
  }

  onBackdropClick(): void {
    this.close();
  }

  onDrawerContentClick(event: Event): void {
    event.stopPropagation();
  }

  /** Bascule l'état replié/déplié de la barre */
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem("eva-notes-todos-collapsed", this.isCollapsed.toString());

    // Si on replie alors qu'un drawer est ouvert, on le ferme
    if (this.isCollapsed && this.isOpen) {
      this.close();
    }

    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Notes navigation
  // ==========================================================================

  /** Change la vue active et persiste en localStorage */
  setActiveView(view: DrawerView): void {
    // Reset la vue conversations quand on quitte cet onglet
    if (this.activeView === "conversations" && view !== "conversations") {
      this.conversationsRef?.resetView();
    }

    this.activeView = view;
    localStorage.setItem("eva-notes-todos-view", view);

    // Reset le mode detail quand on change d'onglet
    if (view !== "notes") {
      this.noteViewMode = "list";
      this.currentNoteId = null;
    }

    this.cdr.markForCheck();
  }

  /** Ouvre une note en mode detail */
  openNote(noteId: string): void {
    this.currentNoteId = noteId;
    this.noteViewMode = "detail";
    this.cdr.markForCheck();
  }

  /** Retrait du filtre contextuel depuis notes-list */
  onContextFilterRemoved(): void {
    this.context = null;
    this.cdr.markForCheck();
  }

  /** Puce de retour des notes : refiltre sur l'objet de la page */
  onContextFilterRestored(): void {
    this.context = this.routeContext;
    this.cdr.markForCheck();
  }

  /** Retour a la liste depuis le detail */
  backToList(): void {
    // Utiliser une microtask pour garantir que le changement se produise dans le bon cycle
    Promise.resolve().then(() => {
      this.noteViewMode = "list";
      this.currentNoteId = null;
      this.cdr.detectChanges();

      // Recharger la liste après le changement de vue
      setTimeout(() => {
        this.notesList?.reload();
      }, 0);
    });
  }

  /** Creer une nouvelle note */
  createNote(): void {
    const newNote: Partial<Note> = {
      title: "Sans titre",
      content: "",
      attachments: [],
    };

    // Pre-remplir le rattachement si contexte actif
    if (this.context) {
      newNote.attachments = [
        {
          object_type: this.context.objectType,
          object_id: this.context.objectId,
          label: this.context.objectLabel,
        },
      ];
    }

    this.noteService
      .create(newNote)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (note) => {
          this.openNote(note.note_id);
        },
        error: () => {
          this.evaService.showError("Impossible de créer la note", "createNote", "");
        },
      });
  }

  /** Clic sur une tâche dans le drawer : le dialog est géré directement par drawer-tasks */
  onTaskClicked(): void {
    // Le composant drawer-tasks ouvre lui-même le dialog de détail — aucune navigation nécessaire
  }

  /** Retourne le titre de la fenêtre selon la vue active */
  getWindowTitle(): string {
    switch (this.activeView) {
      case "tasks":
        return "Tâches";
      case "notes":
        return "Notes";
      case "conversations":
        return "Conversations";
      case "assistant":
        return "Assistant";
      default:
        return "Notes & Todos";
    }
  }
}
